import { CpuChipIcon } from '@heroicons/react/24/outline';
import { Button } from '@leadsales/design-system/react';
import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useIndicatorPresence from './useIndicatorPresence.hook';
import type { tLeadAgentIndicatorStage } from './useLeadAgentIndicator.hook';
import './BotThinkingBubble.styles.scss';

type tBotThinkingBubbleProps = {
  stage: tLeadAgentIndicatorStage | null;
  // The last wait ended with Lead Agent's reply: Done is announced.
  replied: boolean;
  onTakeConversation: () => void;
};

const COPY_KEY: Record<tLeadAgentIndicatorStage, string> = {
  working: 'smartLeadbot.botThinking',
  slow: 'smartLeadbot.botThinkingExtended',
  noReply: 'smartLeadbot.botNoReply',
};

// Today's colors: violet while Lead Agent works, amber once the wait is long.
const TONE = {
  working: {
    border: 'border-violet-400 dark:border-violet-500',
    icon: 'text-violet-600 dark:text-violet-400',
    text: 'text-violet-700 dark:text-violet-300',
    dot: 'bg-violet-500',
  },
  longWait: {
    border: 'border-amber-400 dark:border-amber-500',
    icon: 'text-amber-600 dark:text-amber-400',
    text: 'text-amber-700 dark:text-amber-300',
    dot: 'bg-amber-500',
  },
};

/**
 * Lead Agent's state indicator in the thread (AGI-500), per the "Agent states"
 * table of the DS motion contract (guidelines/motion.md). The pill stays
 * mounted, transparent and empty while there is no wait, so its role="status"
 * region exists before its text arrives: a region inserted with its text
 * already in it is not reliably announced. Each stage's text is announced once,
 * and so is Done when a Lead Agent message ends the wait.
 */
export default function BotThinkingBubble(props: tBotThinkingBubbleProps) {
  const { t } = useTranslation('chat/chat');
  const presence = useIndicatorPresence(props.stage);
  const { stage } = presence;
  const stageText = stage ? t(COPY_KEY[stage]) : '';
  const tone =
    stage === 'slow' || stage === 'noReply' ? TONE.longWait : TONE.working;
  // Done is said, not shown: the status region announces it to screen readers
  // only, while the pill keeps the last stage on screen as it leaves.
  const replied = props.replied && !props.stage;
  const actionRef = useRef<HTMLButtonElement>(null);

  // While the pill leaves, its action drops out of the keyboard, assistive
  // technology and the pointer at once (guidelines/motion.md, The seven
  // patterns). Only the action: the status region stays readable to say Done.
  useEffect(() => {
    if (actionRef.current) actionRef.current.inert = presence.leaving;
  }, [presence.leaving]);

  return (
    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
      <div
        data-testid="lead-agent-indicator"
        className={classNames(
          'BotThinkingBubble ls-motion-essential flex items-center gap-2 bg-white dark:bg-violet-950 border rounded-full px-3.5 py-2 shadow-lg',
          tone.border,
          {
            'BotThinkingBubble--open': presence.open,
            'pointer-events-auto': presence.open && stage === 'noReply',
          },
        )}
      >
        <CpuChipIcon
          aria-hidden="true"
          className={classNames('w-3.5 h-3.5 shrink-0', tone.icon)}
        />
        <span
          role="status"
          className={classNames(
            'text-[11px] font-semibold whitespace-nowrap',
            tone.text,
          )}
        >
          {replied ? (
            <>
              {stageText ? <span aria-hidden="true">{stageText}</span> : null}
              <span className="sr-only">{t('smartLeadbot.botReplied')}</span>
            </>
          ) : (
            stageText
          )}
        </span>
        {stage === 'working' || stage === 'slow' ? (
          <span aria-hidden="true" className="flex items-center gap-1 ml-0.5">
            {[0, 1, 2].map((dot) => (
              <span
                key={dot}
                className={classNames(
                  'BotThinkingBubble__Dot w-1.5 h-1.5 rounded-full',
                  tone.dot,
                )}
              />
            ))}
          </span>
        ) : null}
        {stage === 'noReply' ? (
          <Button ref={actionRef} size="xs" onClick={props.onTakeConversation}>
            {t('smartLeadbot.takeConversation')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
